import { useState, useCallback } from 'react';
import { CalendarDays, AlertTriangle, Printer, ChevronRight, ChevronDown } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { Spinner } from '@/components/ui/Spinner';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { cn } from '@/utils/cn';
import type { DayBookDto } from '@/types/report.types';

/**
 * DayBookPage — everything posted in a period, in the order it happened.
 *
 * Not a replacement for GL Inquiry. GL Inquiry is line-level and filter-heavy: it exists to hunt
 * down one transaction. This is the register you read top to bottom to answer "what happened
 * yesterday", so it stays at voucher level, grouped by day, with each day's own totals.
 *
 * Each day shows Dr = Cr. That is the point of the report: a day whose debits do not equal its
 * credits is a day nobody can sign off, and here you can see it at a glance.
 */

const PAGE_SIZE = 100;

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtAmt = (n: number) => (n === 0 ? '—' : fmt(n));

const fmtDay = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const inputCls = cn(
  'h-8 px-3 rounded-md border text-sm',
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600',
  'focus:outline-none focus:ring-2 focus:ring-brand-500',
);

export const DayBookPage = () => {
  const [fromDate, setFromDate]         = useState(firstOfMonth());
  const [toDate, setToDate]             = useState(today());
  const [locationId, setLocationId]     = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [typeCode, setTypeCode]         = useState('');
  const [pageNumber, setPageNumber]     = useState(1);

  const [book, setBook]       = useState<DayBookDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const run = useCallback(async (page = 1) => {
    setLoading(true); setError(null);
    try {
      const res = await reportApi.getDayBook({
        fromDate, toDate,
        locationId:          locationId   || undefined,
        costCenterId:        costCenterId || undefined,
        transactionTypeCode: typeCode.trim() || undefined,
        pageNumber:          page,
        pageSize:            PAGE_SIZE,
      });
      setBook(res.data.data ?? null);
      setPageNumber(page);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Failed to generate the Day Book.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, locationId, costCenterId, typeCode]);

  const toggle = (date: string) =>
    setCollapsed(s => {
      const next = new Set(s);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });

  const totalPages = book ? Math.max(1, Math.ceil(book.entryCount / PAGE_SIZE)) : 1;

  return (
    <div className="flex flex-col h-full min-h-0">

      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <CalendarDays size={18} className="text-brand-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100">Day Book</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Every posted voucher, in the order it happened</p>
        </div>
        {book && book.days.length > 0 && (
          <button
            onClick={() => window.print()}
            className={cn('h-8 px-3 flex items-center gap-1.5 text-xs rounded-md border',
              'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400',
              'hover:text-brand-600 hover:border-brand-400 transition-colors')}
          >
            <Printer size={13} /> Print
          </button>
        )}
      </div>

      <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700 print:hidden">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Date From">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={cn(inputCls, 'w-36')} />
          </Field>
          <Field label="Date To">
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={cn(inputCls, 'w-36')} />
          </Field>
          <Field label="Type">
            <input type="text" value={typeCode} onChange={e => setTypeCode(e.target.value.toUpperCase())}
                   placeholder="All" maxLength={10} className={cn(inputCls, 'w-24 font-mono uppercase')} />
          </Field>
          <Field label="Location">
            <LocationSelect value={locationId} onChange={setLocationId} className={cn(inputCls, 'w-44')} />
          </Field>
          <Field label="Cost Centre">
            <CostCentreSelect value={costCenterId} onChange={setCostCenterId} className={cn(inputCls, 'w-44')} />
          </Field>
          <button
            onClick={() => run(1)}
            disabled={loading}
            className={cn('h-8 px-4 rounded-md text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors')}
          >
            {loading ? <Spinner size="sm" className="text-white" /> : 'Generate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-5 py-2.5 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800">
          <AlertTriangle size={15} className="shrink-0" /> {error}
        </div>
      )}

      {loading && !book && <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>}

      {!loading && !book && !error && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <CalendarDays size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">Choose a period and click Generate</p>
          </div>
        </div>
      )}

      {book && book.days.length === 0 && !loading && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-gray-400">Nothing was posted in this period.</p>
        </div>
      )}

      {book && book.days.length > 0 && (
        <div className="flex-1 overflow-auto p-5 space-y-4 print:p-0">

          <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{book.companyName}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {book.entryCount} voucher{book.entryCount === 1 ? '' : 's'} in period
                {totalPages > 1 && <> · showing page {pageNumber} of {totalPages}</>}
              </p>
            </div>
            <div className="flex gap-5 text-xs">
              <span className="text-gray-500 dark:text-gray-400">Debit <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">{fmt(book.totalDebit)}</span></span>
              <span className="text-gray-500 dark:text-gray-400">Credit <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">{fmt(book.totalCredit)}</span></span>
            </div>
          </div>

          {book.days.map(day => {
            const isCollapsed = collapsed.has(day.date);
            // A day that does not balance is the one thing this report must never hide.
            const balanced = Math.abs(day.totalDebit - day.totalCredit) < 0.01;
            return (
              <div key={day.date} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => toggle(day.date)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isCollapsed ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmtDay(day.date)}</span>
                    <span className="text-[10px] text-gray-400">{day.entries.length} voucher{day.entries.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="font-mono text-gray-600 dark:text-gray-400">{fmt(day.totalDebit)}</span>
                    <span className="font-mono text-gray-600 dark:text-gray-400">{fmt(day.totalCredit)}</span>
                    {!balanced && (
                      <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-[10px] font-semibold text-rose-700 dark:text-rose-300">
                        Out by {fmt(Math.abs(day.totalDebit - day.totalCredit))}
                      </span>
                    )}
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {day.entries.map(e => (
                      <div key={e.journalId} className="px-4 py-2.5">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-semibold text-gray-600 dark:text-gray-300">{e.transactionTypeCode}</span>
                          <span className="font-mono text-xs text-gray-800 dark:text-gray-200">{e.documentNumber}</span>
                          {e.reference && <span className="text-[10px] text-gray-400 font-mono">{e.reference}</span>}
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{e.narration}</span>
                        </div>
                        <table className="w-full text-xs">
                          <tbody>
                            {e.lines.map((l, i) => (
                              <tr key={i}>
                                <td className="py-0.5 pl-4 w-20 font-mono text-gray-500 dark:text-gray-400">{l.accountCode}</td>
                                <td className="py-0.5 text-gray-700 dark:text-gray-300">{l.accountName}</td>
                                <td className="py-0.5 w-32 text-right font-mono text-gray-700 dark:text-gray-300">{fmtAmt(l.debit)}</td>
                                <td className="py-0.5 w-32 text-right font-mono text-gray-700 dark:text-gray-300">{fmtAmt(l.credit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 print:hidden">
              <PageBtn disabled={pageNumber <= 1 || loading} onClick={() => run(pageNumber - 1)}>Previous</PageBtn>
              <span className="text-xs text-gray-500 dark:text-gray-400">Page {pageNumber} of {totalPages}</span>
              <PageBtn disabled={pageNumber >= totalPages || loading} onClick={() => run(pageNumber + 1)}>Next</PageBtn>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const PageBtn = ({ disabled, onClick, children }: { disabled: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    className={cn('h-7 px-3 rounded-md border text-xs border-gray-300 dark:border-gray-600',
      'text-gray-600 dark:text-gray-400 hover:border-brand-400 hover:text-brand-600',
      'disabled:opacity-40 disabled:cursor-not-allowed transition-colors')}
  >
    {children}
  </button>
);
